import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Form,
  FormLayout,
  TextField,
  Select,
  Text,
  Banner,
  Card,
  BlockStack,
  InlineStack,
  Checkbox,
  RadioButton,
  Badge,
  Divider,
  Autocomplete,
  Icon
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { rentalsAPI, productsAPI, addOnsAPI, customersAPI } from '../services/api';
import shopifyApi from '../services/shopifyApi';
import RentalCalendarComponent from './RentalCalendarComponent';

const RentalModal = ({ 
  isOpen, 
  onClose, 
  preselectedProduct = null, 
  onSuccess 
}) => {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [availableAddOnEntries, setAvailableAddOnEntries] = useState({});
  const [selectedAddOns, setSelectedAddOns] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedProductForCalendar, setSelectedProductForCalendar] = useState(preselectedProduct);
  const [rentalQuantity, setRentalQuantity] = useState('1');
  
  // Bundle configuration state
  const [bundleConfig, setBundleConfig] = useState({});
  const [selectedJacketVariant, setSelectedJacketVariant] = useState(null);
  const [selectedPantsVariant, setSelectedPantsVariant] = useState(null);

  // Form state for new rental
  const [formData, setFormData] = useState({
    customerEmail: '',
    startDate: new Date(),
    endDate: new Date(),
    productId: preselectedProduct ? preselectedProduct.id.toString() : '',
    notes: '',
  });

  const fetchProducts = async () => {
    try {
      const response = await productsAPI.getAll({ limit: 100 });
      setProducts(response.data.products);
    } catch (err) {
      console.error('Products error:', err);
    }
  };

  const fetchCustomers = useCallback(async (searchQuery = '', limit = 100) => {
    try {
      const response = await customersAPI.getAll({ 
        limit: limit,
        search: searchQuery 
      });
      const customersData = response.data.customers || [];
      setCustomers(customersData);
      
      // Update autocomplete options
      const options = customersData.map(customer => ({
        value: customer.id.toString(),
        label: `${customer.firstName || ''} ${customer.lastName || ''} (${customer.email})`.trim()
      }));
      setCustomerOptions(options);
    } catch (err) {
      console.error('Customers error:', err);
    }
  }, []);

  // Debounced search function
  const debouncedSearch = useCallback((searchQuery) => {
    const timeoutId = setTimeout(() => {
      fetchCustomers(searchQuery);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [fetchCustomers]);

  // Handle customer selection from autocomplete
  const handleCustomerSelect = useCallback((selected) => {
    setSelectedCustomerOption(selected);
    
    if (selected.length > 0) {
      const customerId = selected[0];
      const selectedCustomer = customers.find(c => c.id.toString() === customerId);
      
      if (selectedCustomer) {
        setFormData(prev => ({
          ...prev,
          customerId: selectedCustomer.id,
          customerEmail: selectedCustomer.email || '',
          customerFirstName: selectedCustomer.firstName || '',
          customerLastName: selectedCustomer.lastName || '',
          customerPhone: selectedCustomer.phone || '',
        }));
        
        // Set the search query to show the selected customer
        setCustomerSearchQuery(`${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''} (${selectedCustomer.email})`.trim());
      }
    } else {
      // Clear customer data if selection is cleared
      setFormData(prev => ({
        ...prev,
        customerId: '',
        customerEmail: '',
        customerFirstName: '',
        customerLastName: '',
        customerPhone: '',
      }));
      setCustomerSearchQuery('');
    }
  }, [customers]);

  // Handle search query change
  const handleCustomerSearchChange = useCallback((value) => {
    setCustomerSearchQuery(value);
    
    if (value.length > 0) {
      debouncedSearch(value);
    } else {
      // If search is cleared, fetch all customers
      fetchCustomers('', 50);
    }
  }, [debouncedSearch, fetchCustomers]);

  useEffect(() => {
    if (isOpen) {
      fetchProducts();
      fetchCustomers();
      
      // Reset form when modal opens
      setFormData({
        shopifyOrderId: '',
        customerId: '',
        customerEmail: '',
        customerFirstName: '',
        customerLastName: '',
        customerPhone: '',
        startDate: new Date(),
        endDate: new Date(),
        productId: preselectedProduct ? preselectedProduct.id.toString() : '',
        notes: '',
      });
      setSelectedProductForCalendar(preselectedProduct);
      setSelectedAddOns({});
      setAvailableAddOnEntries({});
      setBundleConfig({});
      setSelectedJacketVariant(null);
      setSelectedPantsVariant(null);
      setError('');
      
      // Load product-specific data if a product is preselected
      if (preselectedProduct) {
        fetchProductAddOns(preselectedProduct.id);
        if (preselectedProduct.isBundle && (preselectedProduct.jacketProductId || preselectedProduct.pantsProductId)) {
          fetchBundleProducts(preselectedProduct.jacketProductId, preselectedProduct.pantsProductId);
        }
      } else {
        setAddOns([]);
      }
    }
  }, [isOpen, preselectedProduct, fetchCustomers]);

  const fetchProductAddOns = async (productId) => {
    try {
      const response = await addOnsAPI.getByProduct(productId);
      const addOns = response.data?.addOns || [];
      setAddOns(addOns);
    } catch (err) {
      console.error('Failed to load product add-ons:', err);
      setAddOns([]);
    }
  };

  const fetchBundleProducts = async (jacketProductId, pantsProductId) => {
    try {
      const bundleConfig = {};
      
      if (jacketProductId) {
        try {
          const jacketData = await shopifyApi.getProduct(jacketProductId);
          
          // Handle different response structures
          const jacketProduct = jacketData.product || jacketData;
          
          if (jacketProduct && jacketProduct.id) {
            bundleConfig.jacket = {
              id: jacketProduct.id,
              title: jacketProduct.title,
              variants: (jacketProduct.variants || []).map(v => ({
                id: v.id,
                title: v.title,
                price: v.price
              }))
            };
          } else {
            console.error('Invalid jacket product data:', jacketProduct);
          }
        } catch (err) {
          console.error('Failed to load jacket product:', err);
        }
      }
      
      if (pantsProductId) {
        try {
          const pantsData = await shopifyApi.getProduct(pantsProductId);
          
          // Handle different response structures
          const pantsProduct = pantsData.product || pantsData;
          
          if (pantsProduct && pantsProduct.id) {
            bundleConfig.pants = {
              id: pantsProduct.id,
              title: pantsProduct.title,
              variants: (pantsProduct.variants || []).map(v => ({
                id: v.id,
                title: v.title,
                price: v.price
              }))
            };
          } else {
            console.error('Invalid pants product data:', pantsProduct);
          }
        } catch (err) {
          console.error('Failed to load pants product:', err);
        }
      }
      
      setBundleConfig(bundleConfig);
    } catch (err) {
      console.error('Failed to load bundle products:', err);
    }
  };

  const checkAddOnAvailability = async (startDate, endDate) => {
    if (!startDate || !endDate || addOns.length === 0) {
      setAvailableAddOnEntries({});
      return;
    }

    const availabilityMap = {};
    
    try {
      // Check availability for each add-on
      for (const addOn of addOns) {
        try {
          const response = await addOnsAPI.getAvailableEntries(addOn.id, {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            quantity: parseInt(rentalQuantity) || 1
          });
          availabilityMap[addOn.id] = response.data?.entries || [];
        } catch (err) {
          console.warn(`Failed to check availability for add-on ${addOn.id}:`, err);
          availabilityMap[addOn.id] = [];
        }
      }
      
      setAvailableAddOnEntries(availabilityMap);
    } catch (err) {
      console.error('Error checking add-on availability:', err);
      setAvailableAddOnEntries({});
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // If customer is selected, auto-populate customer details
    if (field === 'customerId') {
      const selectedCustomer = customers.find(c => c.id.toString() === value);
      if (selectedCustomer) {
        setFormData(prev => ({
          ...prev,
          customerId: selectedCustomer.id,
          customerEmail: selectedCustomer.email || '',
          customerFirstName: selectedCustomer.firstName || '',
          customerLastName: selectedCustomer.lastName || '',
          customerPhone: selectedCustomer.phone || '',
        }));
      }
    }
    
    // If product is selected, update the calendar product
    if (field === 'productId') {
      const selectedProduct = products.find(p => p.id.toString() === value);
      setSelectedProductForCalendar(selectedProduct || null);
      
      if (selectedProduct) {
        // Load product-specific add-ons
        fetchProductAddOns(selectedProduct.id);
        
        // Load bundle configuration if it's a bundle product
        if (selectedProduct.isBundle && (selectedProduct.jacketProductId || selectedProduct.pantsProductId)) {
          fetchBundleProducts(selectedProduct.jacketProductId, selectedProduct.pantsProductId);
        } else {
          setBundleConfig({});
        }
      } else {
        // Reset add-ons and bundle config if no product selected
        setAddOns([]);
        setBundleConfig({});
      }
      
      // Reset selections
      setSelectedAddOns({});
      setSelectedJacketVariant(null);
      setSelectedPantsVariant(null);
    }
  };

  const handleCalendarDateSelect = (dates) => {
    if (dates.start && dates.end) {
      setFormData(prev => ({
        ...prev,
        startDate: dates.start,
        endDate: dates.end
      }));
      // Check add-on availability for the selected dates
      checkAddOnAvailability(dates.start, dates.end);
    }
  };

  const handleAddOnSelection = (addOnId, entryId, isSelected) => {
    const addOn = addOns.find(a => a.id === addOnId);
    if (!addOn) return;

    setSelectedAddOns(prev => {
      const newSelection = { ...prev };
      
      if (!newSelection[addOnId]) {
        newSelection[addOnId] = [];
      }

      if (isSelected) {
        if (addOn.allowMultipleSelections) {
          // Check if we haven't exceeded the max selections
          if (newSelection[addOnId].length < addOn.maxSelections) {
            newSelection[addOnId] = [...newSelection[addOnId], entryId];
          }
        } else {
          // Single selection - replace any existing selection
          newSelection[addOnId] = [entryId];
        }
      } else {
        // Remove the selection
        newSelection[addOnId] = newSelection[addOnId].filter(id => id !== entryId);
        if (newSelection[addOnId].length === 0) {
          delete newSelection[addOnId];
        }
      }

      return newSelection;
    });
  };

  const isAddOnEntrySelected = (addOnId, entryId) => {
    return selectedAddOns[addOnId]?.includes(entryId) || false;
  };

  const calculateAddOnPricing = () => {
    if (!formData.startDate || !formData.endDate) return { total: 0, details: [] };
    
    const selectedProduct = products.find(p => p.id.toString() === formData.productId);
    if (!selectedProduct) return { total: 0, details: [] };

    const days = Math.ceil((formData.endDate - formData.startDate) / (1000 * 60 * 60 * 24)) + 1;
    let basePrice = 0;

    // Calculate base product price (simplified - you might want to use the actual pricing logic from the product)
    const priceStages = [selectedProduct.priceStage1, selectedProduct.priceStage2, selectedProduct.priceStage3, selectedProduct.priceStage4]
      .filter(stage => stage && stage.enabled && stage.price > 0)
      .sort((a, b) => b.minDays - a.minDays);
    
    if (priceStages.length > 0) {
      let selectedStage = priceStages[0];
      for (const stage of priceStages) {
        if (days >= stage.minDays) {
          selectedStage = stage;
          break;
        }
      }
      basePrice = selectedStage.price * days;
    }

    let addOnTotal = 0;
    const addOnDetails = [];

    Object.entries(selectedAddOns).forEach(([addOnId, entryIds]) => {
      const addOn = addOns.find(a => a.id === parseInt(addOnId));
      if (!addOn) return;

      entryIds.forEach(entryId => {
        const availableEntries = availableAddOnEntries[addOnId] || [];
        const entry = availableEntries.find(e => e.id === entryId);
        if (!entry) return;

        let entryPrice = 0;
        let priceDescription = 'Included';

        if (entry.pricingType === 'fixed_price') {
          entryPrice = parseFloat(entry.price) * days;
          priceDescription = `$${entry.price}/day × ${days} days = $${entryPrice}`;
        } else if (entry.pricingType === 'percentage_of_base') {
          entryPrice = (basePrice * parseFloat(entry.price)) / 100;
          priceDescription = `${entry.price}% of base price = $${entryPrice}`;
        }

        addOnTotal += entryPrice;
        addOnDetails.push({
          addOnName: addOn.name,
          entryName: entry.displayName || entry.product?.title || 'Unknown',
          price: entryPrice,
          priceDescription
        });
      });
    });

    return { total: addOnTotal, details: addOnDetails };
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      const selectedProduct = products.find(p => p.id.toString() === formData.productId);
      if (!selectedProduct) {
        setError('Please select a product');
        return;
      }

      // Prepare add-on items
      const addOnItems = [];
      Object.entries(selectedAddOns).forEach(([addOnId, entryIds]) => {
        entryIds.forEach(entryId => {
          const availableEntries = availableAddOnEntries[addOnId] || [];
          const entry = availableEntries.find(e => e.id === entryId);
          if (entry) {
            addOnItems.push({
              addOnId: parseInt(addOnId),
              addOnEntryId: entryId,
              productId: entry.rentalProductId,
              quantity: 1 // Add-ons typically have quantity 1
            });
          }
        });
      });

      const rentalData = {
        shopifyOrderId: formData.shopifyOrderId,
        customerId: formData.customerId,
        customerEmail: formData.customerEmail,
        customerFirstName: formData.customerFirstName,
        customerLastName: formData.customerLastName,
        customerPhone: formData.customerPhone,
        rentalItems: [{
          productId: parseInt(formData.productId),
          shopifyProductId: selectedProduct.shopifyProductId,
          quantity: parseInt(rentalQuantity),
          // Bundle configuration
          ...(selectedProduct.isBundle && {
            jacketVariant: selectedJacketVariant,
            pantsVariant: selectedPantsVariant
          })
        }],
        addOnItems: addOnItems,
        startDate: formData.startDate,
        endDate: formData.endDate,
        notes: formData.notes,
      };

      await rentalsAPI.create(rentalData);
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create rental');
    } finally {
      setLoading(false);
    }
  };

  const productOptions = products
    .filter(product => product.id && product.title)
    .map(product => ({
      label: product.title,
      value: product.id.toString(),
    }));


  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={preselectedProduct ? `Book Rental: ${preselectedProduct.title}` : 'Create New Rental'}
      primaryAction={{
        content: loading ? 'Creating...' : 'Create Rental',
        onAction: handleSubmit,
        loading: loading,
        disabled: !formData.customerEmail || !formData.customerFirstName || !formData.customerLastName || !formData.productId
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: onClose,
        },
      ]}
      large
    >
      <Modal.Section>
        <Form onSubmit={handleSubmit}>
          <FormLayout>
            {error && (
              <Banner status="critical" onDismiss={() => setError('')}>
                {error}
              </Banner>
            )}

            <FormLayout.Group>
              <TextField
                label="Shopify Order ID"
                value={formData.shopifyOrderId}
                onChange={(value) => handleFormChange('shopifyOrderId', value)}
                required
              />
              <Autocomplete
                options={customerOptions}
                selected={selectedCustomerOption}
                onSelect={handleCustomerSelect}
                textField={
                  <Autocomplete.TextField
                    label="Customer"
                    value={customerSearchQuery}
                    onChange={handleCustomerSearchChange}
                    placeholder="Search by name or email..."
                    autoComplete="off"
                    prefix={<Icon source={SearchIcon} />}
                    helpText={formData.customerId ? "Customer selected" : "Type to search customers"}
                  />
                }
                emptyState={
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <Text variant="bodyMd" tone="subdued">
                      {customerSearchQuery ? 'No customers found' : 'Start typing to search customers'}
                    </Text>
                  </div>
                }
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <TextField
                label="Customer Email"
                type="email"
                value={formData.customerEmail}
                onChange={(value) => handleFormChange('customerEmail', value)}
                required
                disabled={!!formData.customerId}
                helpText={formData.customerId ? "Auto-populated from selected customer" : ""}
              />
              <TextField
                label="Customer Phone"
                value={formData.customerPhone}
                onChange={(value) => handleFormChange('customerPhone', value)}
                disabled={!!formData.customerId}
                helpText={formData.customerId ? "Auto-populated from selected customer" : ""}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <TextField
                label="First Name"
                value={formData.customerFirstName}
                onChange={(value) => handleFormChange('customerFirstName', value)}
                required
                disabled={!!formData.customerId}
                helpText={formData.customerId ? "Auto-populated from selected customer" : ""}
              />
              <TextField
                label="Last Name"
                value={formData.customerLastName}
                onChange={(value) => handleFormChange('customerLastName', value)}
                required
                disabled={!!formData.customerId}
                helpText={formData.customerId ? "Auto-populated from selected customer" : ""}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <Select
                label="Product"
                options={[{ label: 'Select a product', value: '' }, ...productOptions]}
                value={formData.productId}
                onChange={(value) => handleFormChange('productId', value)}
                required
                disabled={!!preselectedProduct} // Disable if product is preselected
              />
            </FormLayout.Group>

            {/* Bundle Configuration */}
            {selectedProductForCalendar && selectedProductForCalendar.isBundle && Object.keys(bundleConfig).length > 0 && (
              <>
                <Divider />
                <Text variant="headingMd" as="h3">Bundle Configuration</Text>
                
                {bundleConfig.jacket && (
                  <FormLayout.Group>
                    <Select
                      label="Jacket Size"
                      options={[
                        { label: 'Select jacket size', value: '' },
                        ...(bundleConfig.jacket.variants || []).map(variant => ({
                          label: `${variant.title} (€${variant.price})`,
                          value: variant.id.toString()
                        }))
                      ]}
                      value={selectedJacketVariant || ''}
                      onChange={(value) => setSelectedJacketVariant(value)}
                    />
                  </FormLayout.Group>
                )}

                {bundleConfig.pants && (
                  <FormLayout.Group>
                    <Select
                      label="Pants Size"
                      options={[
                        { label: 'Select pants size', value: '' },
                        ...(bundleConfig.pants.variants || []).map(variant => ({
                          label: `${variant.title} (€${variant.price})`,
                          value: variant.id.toString()
                        }))
                      ]}
                      value={selectedPantsVariant || ''}
                      onChange={(value) => setSelectedPantsVariant(value)}
                    />
                  </FormLayout.Group>
                )}
                <Divider />
              </>
            )}

            {/* Date Selection using Calendar Component */}
            <FormLayout.Group>
              <div style={{ width: '100%' }}>
                {selectedProductForCalendar ? (
                  <>
                    <Text variant="bodyMd" fontWeight="bold" style={{ marginBottom: '16px' }}>
                      Select Rental Dates for {selectedProductForCalendar.title}
                    </Text>
                    <RentalCalendarComponent
                      productId={selectedProductForCalendar.id}
                      productData={selectedProductForCalendar}
                      onDateSelect={handleCalendarDateSelect}
                      onQuantityChange={setRentalQuantity}
                    />
                  </>
                ) : (
                  <div style={{ 
                    padding: '20px', 
                    textAlign: 'center', 
                    backgroundColor: '#f6f6f7', 
                    borderRadius: '8px',
                    border: '1px solid #e1e3e5'
                  }}>
                    <Text variant="bodyMd" tone="subdued">
                      Please select a product above to see the rental calendar and availability
                    </Text>
                  </div>
                )}
              </div>
            </FormLayout.Group>

            {/* Add-Ons Selection */}
            {formData.startDate && formData.endDate && addOns.length > 0 && (
              <>
                <Divider />
                <Text variant="headingMd" as="h3">Available Add-Ons</Text>
                <Text variant="bodyMd" tone="subdued" as="p">
                  Select additional items for your rental. Availability and pricing are based on your selected dates.
                </Text>
                
                <BlockStack gap="400">
                  {addOns.map(addOn => {
                    const availableEntries = availableAddOnEntries[addOn.id] || [];
                    const selectedCount = selectedAddOns[addOn.id]?.length || 0;
                    
                    if (availableEntries.length === 0) return null;

                    return (
                      <Card key={addOn.id} sectioned>
                        <BlockStack gap="300">
                          <InlineStack align="space-between">
                            <BlockStack gap="100">
                              <Text variant="headingSm" as="h4">{addOn.name}</Text>
                              {addOn.description && (
                                <Text variant="bodySm" tone="subdued">
                                  {addOn.description}
                                </Text>
                              )}
                              <InlineStack gap="200">
                                {addOn.allowMultipleSelections ? (
                                  <Badge status="info">
                                    Multi-Select (max {addOn.maxSelections})
                                  </Badge>
                                ) : (
                                  <Badge>Single Selection</Badge>
                                )}
                                {selectedCount > 0 && (
                                  <Badge status="success">
                                    {selectedCount} selected
                                  </Badge>
                                )}
                              </InlineStack>
                            </BlockStack>
                          </InlineStack>
                          
                          <BlockStack gap="200">
                            {availableEntries.map(entry => {
                              const isSelected = isAddOnEntrySelected(addOn.id, entry.id);
                              const canSelect = !isSelected && (
                                addOn.allowMultipleSelections 
                                  ? selectedCount < addOn.maxSelections 
                                  : selectedCount === 0
                              );
                              
                              let priceDisplay = 'Included';
                              if (entry.pricingType === 'fixed_price') {
                                priceDisplay = `$${entry.price}/day`;
                              } else if (entry.pricingType === 'percentage_of_base') {
                                priceDisplay = `${entry.price}% of base price`;
                              }

                              const Component = addOn.allowMultipleSelections ? Checkbox : RadioButton;
                              
                              return (
                                <div
                                  key={entry.id}
                                  style={{
                                    padding: '12px',
                                    border: isSelected ? '2px solid #008060' : '1px solid #e1e3e5',
                                    borderRadius: '8px',
                                    backgroundColor: isSelected ? '#f0f9f7' : 'white',
                                    opacity: !canSelect && !isSelected ? 0.6 : 1
                                  }}
                                >
                                  <InlineStack gap="300" align="start">
                                    <Component
                                      id={`addon-${addOn.id}-entry-${entry.id}`}
                                      name={addOn.allowMultipleSelections ? undefined : `addon-${addOn.id}`}
                                      checked={isSelected}
                                      onChange={(checked) => handleAddOnSelection(addOn.id, entry.id, checked)}
                                      disabled={!canSelect && !isSelected}
                                    />
                                    <BlockStack gap="100" style={{ flex: 1 }}>
                                      <InlineStack align="space-between">
                                        <Text variant="bodyMd" fontWeight="semibold">
                                          {entry.displayName || entry.product?.title || 'Unknown Product'}
                                        </Text>
                                        <Text variant="bodyMd" fontWeight="semibold">
                                          &nbsp;({priceDisplay})
                                        </Text>
                                      </InlineStack>
                                      {entry.description && (
                                        <Text variant="bodySm" tone="subdued">
                                          {entry.description}
                                        </Text>
                                      )}
                                    </BlockStack>
                                  </InlineStack>
                                </div>
                              );
                            })}
                          </BlockStack>
                        </BlockStack>
                      </Card>
                    );
                  })}
                </BlockStack>

                {/* Pricing Summary */}
                {Object.keys(selectedAddOns).length > 0 && (
                  <Card sectioned>
                    <BlockStack gap="200">
                      <Text variant="headingSm" as="h4">Add-On Pricing Summary</Text>
                      {(() => {
                        const pricing = calculateAddOnPricing();
                        return (
                          <BlockStack gap="100">
                            {pricing.details.map((detail, index) => (
                              <InlineStack key={index} align="space-between">
                                <Text variant="bodySm">
                                  {detail.addOnName} - {detail.entryName}
                                </Text>
                                <Text variant="bodySm">
                                  {detail.price > 0 ? `$${detail.price.toFixed(2)}` : 'Included'}
                                </Text>
                              </InlineStack>
                            ))}
                            <Divider />
                            <InlineStack align="space-between">
                              <Text variant="bodyMd" fontWeight="semibold">
                                Total Add-On Cost:
                              </Text>
                              <Text variant="bodyMd" fontWeight="semibold">
                                ${pricing.total.toFixed(2)}
                              </Text>
                            </InlineStack>
                          </BlockStack>
                        );
                      })()}
                    </BlockStack>
                  </Card>
                )}
                <Divider />
              </>
            )}

            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(value) => handleFormChange('notes', value)}
              multiline={3}
            />
          </FormLayout>
        </Form>
      </Modal.Section>
    </Modal>
  );
};

export default RentalModal;
